import { LightThemeTableWithPagination } from "components/LightThemeTableWithPagination";
import MultiSelectDropdown from "components/MultiSelectDropdown";
import React, { Component } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { updateSegmentationType } from "state/package-summary";
import { PACKAGE_GROUP, segmentationOptions } from "utils/reposts";
import Dropdown from "../../components/Dropdown";
import { State } from "../../state";
import { fetchPackageSummary, fetchPackageSummaryFilters } from "../../state/package-summary";
import { FIRST_PAGE, PAGE_SIZE, pageSizeOptions } from "helpers/values";

type ReduxProps = ConnectedProps<typeof connecter> & RouteComponentProps;
type PackageSummaryProps = ReduxProps & {
  selectedProjectId: number;
  selectedStores: number[];
  projectId: number;
};

const controllersStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "12px",
  height: "100%",
  minHeight: "100%",
  justifyContent: "space-between",
  alignItems: "center",
};

const lineSeparatorCss: React.CSSProperties = {
  height: "1px",
  width: "100%",
  backgroundColor: "#53598D26",
  margin: "5px auto",
};

const reportContainerCss: React.CSSProperties = {
  height: "400px",
  overflow: "auto",
};

const chartContentCSS: React.CSSProperties = {
  display: "flex",
  width: "100%",
  flexDirection: "column",
};

const controllerDropdownStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
};

class PackageSummary extends Component<
  PackageSummaryProps,
  {
    packageTypes?: (string | number)[];
    segmentType?: string;
    isLoading: boolean;
    // pagination state
    page: number;
    size: number;
  }
> {
  state = {
    packageTypes: [] as (string | number)[],
    segmentType: "" as string,
    isLoading: false,
    page: FIRST_PAGE,
    size: PAGE_SIZE,
  };

  async componentDidMount() {
    const { projectId, selectedProjectId, selectedStores } = this.props;
    await this.loadBaseProjectData(projectId, selectedStores, selectedProjectId);
  }

  async componentDidUpdate(prevProps: PackageSummaryProps) {
    const { projectId, selectedProjectId, selectedStores } = this.props;
    const projectChanged = prevProps.projectId !== projectId;
    const selectedChanged = prevProps.selectedProjectId !== selectedProjectId;
    const storesChanged =
      JSON.stringify(prevProps.selectedStores) !== JSON.stringify(selectedStores);
    if (projectChanged || storesChanged || selectedChanged) {
      await this.loadBaseProjectData(projectId, selectedStores, selectedProjectId);
    }
  }

  loadBaseProjectData = async (
    projectId: number,
    selectedStores: number[],
    selectedProjectId: number
  ) => {
    try {
      this.setState({ isLoading: true });
      this.props.fetchPackageSummaryFilters();
      this.props.fetchPackageSummary(
        projectId,
        [this.props.segmentationType],
        this.state.packageTypes,
        selectedStores,
        FIRST_PAGE,
        PAGE_SIZE,
        selectedProjectId
      );
    } catch (error) {
      console.error("Error loading base project data:", error);
      this.setState({ isLoading: false });
    }
  };

  // Shared helper: fires both APIs with current filter + pagination state
  refetchWithPagination = (page: number, size: number) => {
    const { projectId, selectedProjectId, selectedStores, segmentationType } = this.props;
    const { packageTypes } = this.state;

    this.setState({ isLoading: true, page, size });
    this.props.fetchPackageSummary(
      projectId,
      [segmentationType],
      packageTypes,
      selectedStores,
      page,
      size,
      selectedProjectId
    );
  };

  render() {
    const { selectedProjectId, filters, content, tableLoading, segmentationType } = this.props;

    // true when a comparison project is selected
    const isComparisonMode = selectedProjectId > -1;

    let packageTableData: any[] = [];

    const { packageTypes, page, size } = this.state;
    let packageTypeOptions: any[] = [];

    //  Filters
    if (filters) {
      const { packageGroup } = filters;
      if (packageGroup && packageGroup.length > 0) {
        packageTypeOptions = packageGroup.map((each: any) => ({
          id: each.package_group,
          value: each.package_group,
          label: each.package_group,
        }));
      }
    }

    //  Table data
    if (content?.report?.length > 0) {
      packageTableData = content["report"];
    }

    const transformedOptions = segmentationOptions.map(opt => ({
      ...opt,
      label: opt.label.replace(/_/g, ' ')
    }));

    // Dynamic first column based on selected segmentation type (mirrors SegmentationReport)
    const dynamicColumn = {
      header:
        transformedOptions.find((opt: any) => opt.value === segmentationType)?.label ??
        "Segmentation Type",
      field: "segment",
    };

    //  Primary table columns
    const packageTableColumnsAll = [
      {
        header: "Package Group",
        field: "package_group",
        formatter: (value: number) => (value != null ? `${value}` : "-"),
      },
      dynamicColumn,
      {
        header: "Cubic Share",
        field: "cubic_share_post",
        formatter: (value: number) => (value != null ? `${value}%` : "-"),
      },
      {
        header: "Package Cubic Share Prior",
        field: "cubic_share_prior_package",
        formatter: (value: number) => (value != null ? `${value}%` : "-"),
      },
      {
        header: "Package Share Change",
        field: "package_share_change",
        formatter: (value: number) => (value != null ? `${value}%` : "-"),
      },
      {
        header: "Total Cubic Share Prior",
        field: "total_cubic_share_prior",
        formatter: (value: number) => (value != null ? `${value}%` : "-"),
      },
      {
        header: "Total Cubic Share Post",
        field: "cubic_share_post",
        formatter: (value: number) => (value != null ? `${value}%` : "-"),
      },
      {
        header: "Total Share Change",
        field: "total_cubic_share_change",
        formatter: (value: number) => (value != null ? `${value}%` : "-"),
      },
      { header: "Adds", field: "adds" },
      { header: "Deletes", field: "deletes" },
      { header: "Maintains", field: "maintains" },
      { header: "Total PODs", field: "total_pods_post" },
      { header: "POD changes", field: "pod_change" },
      { header: "Adds Per Store", field: "adds_per_store" },
      { header: "Deletes Per Store", field: "deletes_per_store" },
    ];

    const packageTableColumns = [
      packageTableColumnsAll[0],
      packageTableColumnsAll[1],
      packageTableColumnsAll[2],
      packageTableColumnsAll[11],
    ];

    //  Comparison table columns
    const comparisonTableColumns = [
      { header: "Package Group", field: "package_group" },
      dynamicColumn,
      {
        header: "Cubic Share Post",
        field: "cubic_share_post",
        formatter: (v: number) => (v != null ? `${v}%` : "-"),
      },
      {
        header: "Cubic Share Prior",
        field: "cubic_share_prior",
        formatter: (v: number) => (v != null ? `${v}%` : "-"),
      },
      {
        header: "Share Change",
        field: "share_change",
        formatter: (v: number) => (v != null ? `${v}%` : "-"),
      },
      { header: "Adds", field: "adds" },
      { header: "Deletes", field: "deletes" },
      { header: "Maintains", field: "maintains" },
      { header: "Total PODs", field: "total_pods_post" },
      { header: "POD Change", field: "pod_change" },
      { header: "Adds Per Store", field: "adds_per_store" },
      { header: "Deletes Per Store", field: "deletes_per_store" },
    ];

    //  Handlers
    const handleSegmentTypeChange = (value: string) => {
      this.props.updateSegmentationType(value);
      this.setState({ isLoading: true, page: 1, size });
      this.props.fetchPackageSummary(
        this.props.projectId,
        [value],
        packageTypes,
        this.props.selectedStores,
        1,
        size,
        selectedProjectId
      );
    };

    const handlePackageTypesChange = (values: any) => {
      this.setState({ packageTypes: values, page: 1 }, () => {
        this.refetchWithPagination(1, this.state.size);
      });
    };

    const handlePageChange = (newPage: number, newSize: number) => {
      const totalCount = content?.count ?? 0;
      const totalPages = Math.ceil(totalCount / newSize) || 1;
      // Guard: don't call API if already on this page, or if page is out of bounds
      const isCurrentPage = newPage === page && newSize === size;
      const isOutOfBounds = newPage < 1 || (totalPages > 0 && newPage > totalPages);
      if (isCurrentPage || isOutOfBounds) return;
      this.refetchWithPagination(newPage, newSize);
    };

    return (
      <div className="reset-recap-container">
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to   { transform: rotate(360deg); }
            }
            .chart-styles table thead tr th {
                position: sticky !important;
                top: 0 !important;
                z-index: 1;
                background-color: #F9FAFB;
            }
            .chart-styles > div > div {
                max-height: calc(100vh - 330px) !important;
            }
            .chart-styles table tbody tr td {
              height: 45px !important;
              vertical-align: middle;
              min-width: 100px;
            }
          `}
        </style>
        <div className="store-summary-header">
          <div style={controllersStyle}>
            <div>
              Package Summary Report
              {/* <div className="reset-recap-sub-header">
                Highlights space efficiency to inform portfolio and space allocation decisions.
              </div> */}
            </div>
            <div className="package-filters" style={controllerDropdownStyle}>
              <div style={{ marginRight: "8px", width: "200px" }}>
                <div className="header-controller-dropdown">Select Packages</div>
                <MultiSelectDropdown
                  options={packageTypeOptions}
                  value={packageTypes}
                  onChange={(value) => {
                    const newValue = Array.isArray(value) ? value : [value];
                    this.setState({ packageTypes: newValue }, () => {
                      handlePackageTypesChange(newValue);
                    });
                  }}
                  placeholder="All Types"
                />
              </div>
              <div style={{ marginRight: "8px", width: "200px" }}>
                <div className="header-controller-dropdown">Select Segmentation</div>
                <Dropdown
                  value={transformedOptions.find((o: any) => o.value === segmentationType) ?? null}
                  options={transformedOptions.filter((o) => o.value !== PACKAGE_GROUP)}
                  onChange={(selected: any) => {
                    handleSegmentTypeChange(selected.value);
                  }}
                  placeholder="All Types"
                  isSearchable
                  controlStyles={{ minHeight: 34, borderRadius: "16px" }}
                  noShadow={true}
                />
              </div>
            </div>
          </div>
        </div>

        <div style={lineSeparatorCss} />
        <div className="reset-recap-chart-container" style={reportContainerCss}>
          <div className="charts-row" style={chartContentCSS}>
            {/*  Primary table — hidden when a comparison project is selected  */}
            {!isComparisonMode && (
              <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
                {/* <div className="chart-headers">Space Summary by Package Selection</div>
                <div className="chart-sub-header">
                  Highlights space efficiency to inform portfolio and space allocation decisions.
                </div> */}
                <LightThemeTableWithPagination
                  data={packageTableData}
                  columns={packageTableColumns}
                  pageSize={size}
                  pageSizeOptions={pageSizeOptions}
                  showPagination={true}
                  isLoading={tableLoading}
                  totalPages={Math.ceil((content?.count ?? 0) / size) || 1}
                  currentPage={page}
                  onPageChange={handlePageChange}
                  onClickOnRow={(row) => console.log("Clicked row:", row)}
                />
              </div>
            )}

            {/*  Comparison table — shown only when a comparison project is selected  */}
            {isComparisonMode && (
              <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
                <LightThemeTableWithPagination
                  data={packageTableData}
                  columns={comparisonTableColumns}
                  pageSize={size}
                  pageSizeOptions={pageSizeOptions}
                  showPagination={true}
                  isLoading={tableLoading}
                  totalPages={Math.ceil((content?.count ?? 0) / size) || 1}
                  currentPage={page}
                  onPageChange={handlePageChange}
                  onClickOnRow={(row) => console.log("Clicked comparison row:", row)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const connecter = connect(
  (state: State) => ({
    filters: state.packageSummaryDetails.filters,
    content: state.packageSummaryDetails.content,
    loading: state.packageSummaryDetails.loading,
    tableLoading: state.packageSummaryDetails.tableLoading,
    segmentationType: state.packageSummaryDetails.segmentationType,
  }),
  { fetchPackageSummaryFilters, fetchPackageSummary, updateSegmentationType }
);

export default connecter(withRouter(PackageSummary));
